// API Response Types
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  errors?: string[];
}

// User Types
export enum UserRole {
  Admin = 1,
  Client = 2
}

export enum Gender {
  Male = 1,
  Female = 2
}

export interface UserResponse {
  id: number;
  name: string;
  email: string;
  phone: string;
  phoneNumber?: string;
  role: UserRole;
  isActive: boolean;
  adminNotificationEnabled: boolean;
  isMorningClient: boolean;
  isAfternoonClient: boolean;
  cardiologyProof: boolean;
  gender?: Gender | null;
  preferredLanguage?: string;
  createdAt: string;
  subscription?: SubscriptionInfo;
  monthlyLimit?: number;
  appointments?: AppointmentResponse[];
}

export interface SubscriptionInfo {
  id: number;
  userId: number;
  startDate: string;
  endDate: string;
  actualStartDate?: string;
  actualEndDate?: string;
  monthlyLimit: number;
  isActive: boolean;
  isActivated: boolean;
  isFrozen: boolean;
  frozenAt?: string;
  unfrozenAt?: string;
  remainingDaysAtFreeze?: number;
  validAppointmentCount: number;
  upcomingAppointmentCount?: number;
  completedAppointmentCount?: number;
  attendedAppointmentCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionHistoryItem {
  id: number;
  startDate: string;
  endDate: string;
  actualStartDate?: string;
  actualEndDate?: string;
  monthlyLimit: number;
  isActive: boolean;
  isFrozen: boolean;
  frozenAt?: string;
  unfrozenAt?: string;
  remainingDaysAtFreeze?: number;
  createdAt: string;
  isActivated: boolean;
  attendedAppointmentCount: number;
  validAppointmentCount: number;
}

// Auth Types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  name: string;
  email: string;
  role: UserRole;
  expiresAt: string;
}

// Registration Types
export interface RegisterRequest {
  name: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  turnstileToken?: string;
}

// Email Confirmation Types
export interface ConfirmEmailRequest {
  email: string;
  token: string;
}

export interface ResendVerificationEmailRequest {
  email: string;
  turnstileToken?: string;
}

// Password Reset Types
export interface ForgotPasswordRequest {
  email: string;
  turnstileToken?: string;
}

export interface ResetPasswordRequest {
  email: string;
  token: string;
  newPassword: string;
  confirmPassword: string;
}

// Appointment Types
export enum AppointmentStatus {
  Active = 1,
  Cancelled = 2,
  Completed = 3
}

export interface AppointmentResponse {
  id: number;
  userId: number;
  userName: string;
  appointmentDate: string;
  startTime: string;
  endTime: string;
  status: AppointmentStatus;
  bookedAt: string;
  cancelledAt?: string;
  cancelledBy?: number;
  bookedBy: number;
  bookedByName: string;
}

export interface BookAppointmentRequest {
  appointmentDate: string;
  startTime: string;
  endTime: string;
  userId?: number;
  isAdminOverride?: boolean;
}

// Client Management Types
export interface CreateClientRequest {
  name: string;
  email: string;
  phone: string;
  password: string;
  isActive?: boolean;
  cardiologyProof?: boolean;
  gender?: Gender | null;
}

export interface UpdateClientRequest {
  name: string;
  email: string;
  phone: string;
  isActive?: boolean;
  adminNotificationEnabled?: boolean;
  isMorningClient?: boolean;
  isAfternoonClient?: boolean;
  cardiologyProof?: boolean;
  gender?: Gender | null;
}

// Subscription Types
export interface CreateSubscriptionRequest {
  startDate: string;
  endDate: string;
  monthlyLimit: number;
  isActive?: boolean;
}

// Timeslot Types
export interface TimeslotInfo {
  id: number;
  date: string;
  originalStartTime?: string;
  originalEndTime?: string;
  startTime: string;
  endTime: string;
  capacity: number;
  currentBookings: number;
  isAvailable: boolean;
  isGymOpen?: boolean;
  isDisabled?: boolean;
  disabledReason?: string;
  appointments: AppointmentResponse[];
  hasOverride?: boolean;
  hasTimeChange?: boolean;
  hasCapacityOverride?: boolean;
  overrideReason?: string;
}

// New capacity response structure from backend
export interface TimeslotCapacityInfo {
  originalStartTime: string;
  originalEndTime: string;
  startTime: string;
  endTime: string;
  isGymOpen: boolean;
  isDisabled: boolean;
  disabledReason?: string;
  totalCapacity: number;
  currentBookings: number;
  remainingSlots: number;
  isAvailable: boolean;
  hasOverride: boolean;
  hasTimeChange: boolean;
  hasCapacityOverride: boolean;
  overrideReason?: string;
}

export interface TimeslotCapacityResponse {
  date: string;
  dayOfWeek: string;
  totalCapacity: number;
  timeslots: TimeslotCapacityInfo[];
  isFullyDisabled: boolean;
  disabledReason?: string;
  hasOverrides: boolean;
  overrideCount: number;
}

export interface UpdateTimeslotRequest {
  capacity?: number;
  isAvailable?: boolean;
}

export interface TimeslotSchedule {
  dayOfWeek: number; // 0 = Sunday, 1 = Monday, etc.
  startTime: string;
  endTime: string;
  capacity: number;
  isActive: boolean;
}

// Admin Timeslot Management Types
export interface TimeSpan {
  ticks: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  milliseconds: number;
  totalDays: number;
  totalHours: number;
  totalMinutes: number;
  totalSeconds: number;
  totalMilliseconds: number;
}

export interface UpdateTimeslotCapacityRequest {
  date: string;
  startTime: TimeSpan;
  endTime: TimeSpan;
  newCapacity: number;
  reason?: string;
}

export interface UpdateTimeslotTimeRequest {
  date: string;
  originalStartTime: TimeSpan;
  originalEndTime: TimeSpan;
  newStartTime: TimeSpan;
  newEndTime: TimeSpan;
  reason?: string;
}

export interface DisableTimeslotRequest {
  date: string;
  startTime: TimeSpan;
  endTime: TimeSpan;
  reason?: string;
}

export interface DisableDayRequest {
  date: string;
  reason?: string;
}

export interface DisableDateRangeRequest {
  startDate: string;
  endDate: string;
  reason?: string;
}

export interface TimeslotOverrideResponse {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
  isDisabled: boolean;
  capacityOverride?: number;
  newStartTime: string | null;
  newEndTime: string | null;
  reason?: string;
  createdBy: number;
  createdByName: string;
  createdAt: string;
  isFullDayOverride: boolean;
}

// Settings Types
export interface GlobalSetting {
  id: number;
  key: string;
  value: string;
  updatedAt: string;
}

// Email Template Types
export enum EmailTemplateType {
  Welcome = 1,
  Reminder = 2,
  Cancellation = 3
}

export interface EmailTemplate {
  id: number;
  templateType: EmailTemplateType;
  subject: string;
  body: string;
  isActive: boolean;
  updatedAt: string;
}

// Monthly Limit Override Types
export interface MonthlyLimitOverrideRequest {
  newMonthlyLimit: number;
}

// Form state types for components
export interface ClientFormData {
  name: string;
  email: string;
  phone: string;
  password: string;
  isActive: boolean;
}

export interface SubscriptionFormData {
  startDate: string;
  endDate: string;
  monthlyLimit: number;
  isActive: boolean;
}

export interface UpdateSubscriptionRequest {
  startDate: string;
  endDate: string;
  // null while the subscription hasn't been activated yet (it activates on the first booking).
  actualStartDate: string | null;
  actualEndDate: string | null;
  monthlyLimit: number;
  isActive: boolean;
}

export interface UpdateSubscriptionFormData {
  startDate: string;
  endDate: string;
  actualStartDate: string;
  actualEndDate: string;
  monthlyLimit: number;
  isActive: boolean;
}

export interface Subscription {
  id: number;
  startDate: string;
  endDate: string;
  monthlyLimit: number;
  isActive: boolean;
}

export interface AppointmentFormData {
  appointmentDate: string;
  startTime: string;
  endTime: string;
  userId?: number;
}

// Timeslot Interest Types
export interface ExpressInterestRequest {
  appointmentDate: string;
  startTime: string;
  endTime: string;
}

export interface TimeslotInterestResponse {
  id: number;
  userId: number;
  appointmentDate: string;
  startTime: string;
  endTime: string;
  expressedAt: string;
}

// Timeslot Clients Types
export interface TimeslotClientsResponse {
  date: string;
  originalStartTime: string;
  originalEndTime: string;
  effectiveStartTime: string;
  effectiveEndTime: string;
  hasTimeOverride: boolean;
  overrideReason: string | null;
  totalCapacity: number;
  bookedCount: number;
  bookedClients: AppointmentResponse[];
}

export interface TimeslotClientsBatchItem {
  startTime: string;
  endTime: string;
  bookedCount: number;
  bookedClients: AppointmentResponse[];
}

export interface TimeslotClientsBatchResponse {
  date: string;
  timeslots: TimeslotClientsBatchItem[];
}

// Global Timeslot Management Types
export enum DayOfWeek {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6
}

export interface GlobalTimeslotDefinitionResponse {
  id: number;
  originalStartTime: string;
  originalEndTime: string;
  effectiveStartTime: string;
  effectiveEndTime: string;
  defaultCapacity: number;
  isEnabled: boolean;
  dayOfWeek: DayOfWeek;
  dayOfWeekName: string;
  reason?: string;
  modifiedBy: number;
  modifiedByName?: string;
  modifiedAt: string;
  isModified: boolean;
  isCustomTimeslot: boolean;
  originalTimeRange: string;
  effectiveTimeRange: string;
  hasTimeModification: boolean;
}

export interface CreateGlobalTimeslotRequest {
  startTime: string;
  endTime: string;
  capacity: number;
  dayOfWeek?: DayOfWeek;
  reason?: string;
}

export interface UpdateGlobalTimeslotTimeRequest {
  newStartTime: string;
  newEndTime: string;
  dayOfWeek?: DayOfWeek;
  reason?: string;
}

export interface UpdateGlobalTimeslotCapacityRequest {
  newCapacity: number;
  dayOfWeek?: DayOfWeek;
  reason?: string;
}

export interface UpdateGlobalTimeslotEnabledRequest {
  isEnabled: boolean;
  dayOfWeek?: DayOfWeek;
  reason?: string;
}

export interface BulkTimeslotUpdate {
  originalStartTime: string;
  originalEndTime: string;
  newStartTime?: string;
  newEndTime?: string;
  newCapacity?: number;
  isEnabled?: boolean;
  dayOfWeek: DayOfWeek;
}

export interface BulkUpdateGlobalTimeslotsRequest {
  updates: BulkTimeslotUpdate[];
  reason?: string;
}

// Broadcast Email Types
export interface BroadcastEmailRequest {
  subject: string;
  message: string;
  activeClientsOnly?: boolean;
  targetClientIds?: number[] | null;
  scheduledSendTime?: string;
  attachmentIds?: number[] | null;
}

export interface BroadcastEmailResponse {
  success: boolean;
  message: string;
  data: null;
  errors: string[] | null;
}

export interface EmailAttachmentResponse {
  id: number;
  fileName: string;
  contentType: string;
  fileSize: number;
}

// Language Preference Types
export interface UpdateLanguagePreferenceRequest {
  languageCode: 'en' | 'el';
}

// Cancellation Deadline Types
export interface CancellationDeadlineSettingsResponse {
  morningClientCancellationDeadlineTime: string;
  regularClientCancellationDeadlineTime: string;
  morningAppointmentCutoffTime: string;
  // Booking boundaries, "HH:mm". Morning clients book strictly before the first,
  // afternoon clients at or after the second.
  morningClientBookingCutoffTime: string;
  afternoonClientBookingStartTime: string;
}

// Body Fat Measurement Types
export interface BodyFatMeasurementResponse {
  id: number;
  userId: number;
  measurementDate: string;
  lastMeasurementDate: string | null;
  emailSent: boolean;
  emailSentAt: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface CreateBodyFatMeasurementRequest {
  measurementDate: string;
}

export interface UpdateBodyFatMeasurementRequest {
  measurementDate: string;
}
